import { createElement } from "react";
import { createRoute, lazyRouteComponent } from "@tanstack/react-router";

import {
  SUPERVISOR_ASSIGNMENTS_CREATE,
  SUPERVISOR_ASSIGNMENTS_UPDATE,
  SUPERVISOR_ASSIGNMENTS_VIEW,
} from "../../../../permissions";
import { requirePermission } from "../../../../presentation/routes/requirePermission";
import type { SupervisorAssignmentsRouteFactory } from "./supervisorAssignmentsRouteContext";
import {
  supervisorAssignmentHistorySearchSchema,
  supervisorAssignmentsSearchSchema,
} from "./searchSchemas";

const SupervisorAssignmentsListScreen = lazyRouteComponent(
  () => import("../SupervisorAssignmentsListScreen"),
  "SupervisorAssignmentsListScreen",
);
const CreateSupervisorAssignmentScreen = lazyRouteComponent(
  () => import("../CreateSupervisorAssignmentScreen"),
  "CreateSupervisorAssignmentScreen",
);
const SupervisorAssignmentHistoryScreen = lazyRouteComponent(
  () => import("../SupervisorAssignmentHistoryScreen"),
  "SupervisorAssignmentHistoryScreen",
);
const SupervisorAssignmentDetailScreen = lazyRouteComponent(
  () => import("../SupervisorAssignmentDetailScreen"),
  "SupervisorAssignmentDetailScreen",
);
const EditSupervisorAssignmentScreen = lazyRouteComponent(
  () => import("../EditSupervisorAssignmentScreen"),
  "EditSupervisorAssignmentScreen",
);

export const createSupervisorAssignmentsRoutes: SupervisorAssignmentsRouteFactory =
  ({ context, basePath, supervisorAssignments, branches }) => {
    // '$id' is the most general path here -- kept last, and after the
    // literal 'create'/'history' segments below, so it's never captured as a
    // supervisor assignment id (same convention as sensorAssignments'
    // editRoute/detailRoute ordering). It's actually the supervisor's own
    // id -- matches GET .../supervisors/:supervisorId.
    const detailRoute = createRoute({
      getParentRoute: context.getParentRoute,
      path: `${basePath}/$id`,
      beforeLoad: requirePermission([SUPERVISOR_ASSIGNMENTS_VIEW]),
      component: function SupervisorAssignmentDetailRouteScreen() {
        const { id } = detailRoute.useParams() as unknown as { id: string };
        return createElement(SupervisorAssignmentDetailScreen, {
          repository: supervisorAssignments,
          basePath,
          supervisorId: id,
        });
      },
    });

    // '$id/edit' is registered before the bare '$id' route below, same
    // convention as sensorAssignments' editRoute/detailRoute ordering. It's
    // the supervisor's own id, same as detailRoute above.
    const editRoute = createRoute({
      getParentRoute: context.getParentRoute,
      path: `${basePath}/$id/edit`,
      beforeLoad: requirePermission([SUPERVISOR_ASSIGNMENTS_UPDATE]),
      component: function SupervisorAssignmentEditRouteScreen() {
        const { id } = editRoute.useParams() as unknown as { id: string };
        return createElement(EditSupervisorAssignmentScreen, {
          repository: supervisorAssignments,
          branches,
          basePath,
          supervisorId: id,
        });
      },
    });

    return [
      createRoute({
        getParentRoute: context.getParentRoute,
        path: basePath,
        validateSearch: supervisorAssignmentsSearchSchema,
        beforeLoad: requirePermission([SUPERVISOR_ASSIGNMENTS_VIEW]),
        component: () =>
          createElement(SupervisorAssignmentsListScreen, {
            repository: supervisorAssignments,
            basePath,
          }),
      }),
      // 'create' and 'history' are literal segments, registered before '$id'
      // so neither is ever captured as a supervisor assignment id (same
      // convention as sensorAssignments/devices/assetTypes).
      createRoute({
        getParentRoute: context.getParentRoute,
        path: `${basePath}/create`,
        beforeLoad: requirePermission([SUPERVISOR_ASSIGNMENTS_CREATE]),
        component: () =>
          createElement(CreateSupervisorAssignmentScreen, {
            repository: supervisorAssignments,
            branches,
            basePath,
          }),
      }),
      createRoute({
        getParentRoute: context.getParentRoute,
        path: `${basePath}/history`,
        validateSearch: supervisorAssignmentHistorySearchSchema,
        beforeLoad: requirePermission([SUPERVISOR_ASSIGNMENTS_VIEW]),
        component: () =>
          createElement(SupervisorAssignmentHistoryScreen, {
            repository: supervisorAssignments,
            basePath,
          }),
      }),
      editRoute,
      detailRoute,
    ];
  };
